<template>
  <div id="app">
    <div id="header-placeholder"></div>

    <div class="login-overlay" id="loginOverlay">
      <div class="login-popup">
        <button class="close-popup" @click="closeLoginPopup">✕</button>
        <h3>Sign in to start!</h3>
        <p>It's completely free</p>
        <div class="login-buttons">
          <a href="https://www.customteamkit.com/blog/wp-login.php?action=register&redirect_to=https://www.customteamkit.com/index.html" class="login-btn register">Register</a>
          <a href="https://www.customteamkit.com/blog/wp-login.php?redirect_to=https://www.customteamkit.com/index.html" class="login-btn login">Log In</a>
        </div>
      </div>
    </div>

    <div class="container">
      <div class="designer-layout">
        <!-- 3D 视图区域 -->
        <div class="viewer-wrapper">
          <Viewer3D
            ref="viewer3d"
            @select-part="handleSelectPart"
            @select-logo="handleSelectLogo"
            @clear-logo-selection="handleClearLogoSelection"
            @add-image-to-part="handleAddImageToPart"
            @delete-text="handleDeleteText"
            @delete-logo="handleDeleteLogo"
            @logo-move-end="handleLogoMoveEnd"
            @model-loaded="handleModelLoaded"
          />
        </div>

        <!-- 右侧面板：“步骤选项卡（Tabs / Steps）” 1. Design 2. Review 3. Roster-->
        <div class="right-panel">
          <!-- ========== 第一层：设计步骤导航（Horizontal Tabs） ========== -->
          <div class="design-tabs">
            <!-- Design 按钮 -->
            <button 
              :class="['tab-btn tab-design', { active: activeTab === 'design' }]"
              @click="activeTab = 'design'"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 3l4 4L7 21H3v-4L17 3z"/>
                <path d="M15 5l4 4"/>
              </svg>
              <span>1. Design</span>
            </button>
            
            <!-- 双箭头 1：Design → Review -->
            <div class="arrow-next" @click="activeTab = 'review'">
              <img src="/images/arrow01.png" alt="Next" class="arrow-icon">
            </div>
            
            <!-- Review 按钮 -->
            <button 
              :class="['tab-btn tab-review', { active: activeTab === 'review' }]"
              @click="activeTab = 'review'"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <path d="M8 10h.01M12 10h.01M16 10h.01"/>
              </svg>
              <span>2. Review</span>
            </button>
            
            <!-- 双箭头 2：Review → Roster -->
            <div class="arrow-next" @click="activeTab = 'roster'">
              <img src="/images/arrow01.png" alt="Next" class="arrow-icon">
            </div>
            
            <!-- Roster 按钮 -->
            <button 
              :class="['tab-btn tab-roster', { active: activeTab === 'roster' }]"
              @click="activeTab = 'roster'"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span>3. Roster</span>
            </button>
          </div>

          <!-- ========== 第二层：动态内容切换区 ========== -->
          
          <!-- 1. Design 标签页 -->
          <div v-show="activeTab === 'design'" class="tab-content">
            <!-- Short Sleeve 专属页面：无款式选择器，固定 Short Sleeve -->
            <PartSelector
              ref="partSelector"
              :current-garment="currentGarment"
              :current-selected-part="currentSelectedPart"
              @select-part="handleSelectPart"
              @part-clicked="handleDirectPartSelect"
            />
            <ColorPalette
              :current-selected-part="currentSelectedPart"
              @select-color="handleSelectColor"
            />
            <LogoUploader
              ref="logoUploader"
              :current-selected-part="currentSelectedPart"
              @add-image="handleAddImageToPart"
            />
            <TextEditor
              @update-text="handleUpdateText"
              @force-composite="handleForceComposite"
            />
          </div>

          <!-- 2. Review 标签页 -->
          <div v-show="activeTab === 'review'" class="tab-content">
            <!-- Short Sleeve 专属页面：无款式选择器 -->
            <!-- Comments 评论区 -->
            <Comments
              ref="comments"
              :current-garment="currentGarment"
            />
          </div>

          <!-- 3. Roster 标签页 -->
          <div v-show="activeTab === 'roster'" class="tab-content">
            <RosterManager ref="rosterManager" :show-type="false" />
          </div>

          <!-- ========== 第三层：固定的行动触点 ========== -->
          <div class="fixed-action-bar">
            <SaveButton
              ref="saveButton"
              :design-id="currentDesignId"
              :current-garment="currentGarment"
              :garment-designs="garmentDesignsForProps"
              :user-colors="userColorsForProps"
              :part-layers-map="partLayersMapForProps"
              :text-items="textItems"
              :next-id="nextId"
              roster-path="/cyclingjerseyroster"
              review-path="/cyclingjerseys"
              @design-saved="handleDesignSaved"
              @sync-garment-data="syncCurrentGarmentData"
            />
          </div>
        </div>
      </div>
    </div>

    <div id="footer-placeholder"></div>

    <!-- 浮动联系按钮 -->
    <div class="floating-contact" id="floatingContact">
      <button class="contact-toggle" @click="toggleContactMenu" aria-label="Contact options" :aria-expanded="contactMenuOpen">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>
      </button>
      <div class="contact-options" :class="{ 'open': contactMenuOpen }">
        <a href="https://wa.me/8613537631227" target="_blank" class="contact-option whatsapp">
          <i class="fab fa-whatsapp"></i>
          <span>WhatsApp</span>
        </a>
        <a href="mailto:68534150@QQ.com" class="contact-option email">
          <i class="fas fa-envelope"></i>
          <span>Email</span>
        </a>
        <a href="tel:+8613537631227" class="contact-option phone">
          <i class="fas fa-phone"></i>
          <span>Call</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useDesignStore } from '@/stores/designStore'
import ColorPalette from '@/components/ColorPalette.vue'
import PartSelector from '@/components/PartSelector.vue'
import LogoUploader from '@/components/LogoUploader.vue'
import TextEditor from '@/components/TextEditor.vue'
import Comments from '@/components/Comments.vue'
import SaveButton from '@/components/SaveButton.vue'
import Viewer3D from '@/components/Viewer3D.vue'
import RosterManager from '@/components/RosterManager.vue'
import RosterPage from '@/views/RosterPage.vue'

export default {
  name: 'cyclingjersey',
  components: {
    ColorPalette,
    PartSelector,
    LogoUploader,
    TextEditor,
    Comments,
    SaveButton,
    Viewer3D,
    RosterManager,
    RosterPage,
  },
  data() {
    return {
      isRosterPage: false,
      contactMenuOpen: false,
      garmentDesignsForProps: {},
      userColorsForProps: new Map(),
      partLayersMapForProps: new Map(),
      currentSelectedPartLocal: 'front',
      currentEditingLogoLocal: null,
      persistentDesignLink: '',
      persistentDesignLinkShort: '',
      activeTab: 'design'
    }
  },
  computed: {
    ...mapState(useDesignStore, [
      'currentGarment',
      'currentDesignId',
      'userColors',
      'partLayersMap',
      'textItems',
      'nextId',
      'garmentDesigns'
    ]),
    currentSelectedPart() {
      return this.currentSelectedPartLocal
    },
    currentEditingLogo() {
      return this.currentEditingLogoLocal
    }
  },
  watch: {
    userColors: {
      handler(newVal) {
        this.userColorsForProps = newVal
      },
      deep: true,
      immediate: true
    },
    partLayersMap: {
      handler(newVal) {
        this.partLayersMapForProps = newVal
      },
      deep: true,
      immediate: true
    },
    garmentDesigns: {
      handler(newVal) {
        const designs = {}
        for (const [key, value] of Object.entries(newVal)) {
          designs[key] = {
            colors: value.colors,
            layers: value.layers,
            texts: value.texts,
            nextId: value.nextId
          }
        }
        this.garmentDesignsForProps = designs
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.loadHeaderFooter()
    this.loadSharedDesign()
    this.setupOutsideClick()


    const urlParams = new URLSearchParams(window.location.search)
    const tabParam = urlParams.get('tab')
    if (tabParam === 'review') {
      this.activeTab = 'review'
    } else if (tabParam === 'roster') {
      this.activeTab = 'roster'
    }


    const store = useDesignStore()
    this.currentSelectedPartLocal = store.currentSelectedPart
    this.currentEditingLogoLocal = store.currentEditingLogo
    store.$subscribe((mutation, state) => {
      this.currentSelectedPartLocal = state.currentSelectedPart
      this.currentEditingLogoLocal = state.currentEditingLogo
    })
    this.$nextTick(() => {
      console.log('Viewer3D ref:', this.$refs.viewer3d)
    })
  },
  methods: {

    handleDesignSaved({ designId, link }) {
      console.log('Design saved:', designId, link)
      this.persistentDesignLink = link
      this.persistentDesignLinkShort = link.length > 30 ? link.substring(0, 30) + '...' : link
      if (this.$refs.logoUploader) {
        this.$refs.logoUploader.saveImageGallery()
      }
    },
    copyPersistentLink() {
      if (!this.persistentDesignLink) return
      navigator.clipboard.writeText(this.persistentDesignLink).then(() => {
        alert('Design link copied to clipboard!')
      }).catch(() => {
        const textarea = document.createElement('textarea')
        textarea.value = this.persistentDesignLink
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        alert('Design link copied to clipboard!')
      })
    },

    handleForceComposite({ part, id }) {
      console.log('App: Force composite texture for', part, id)
      if (this.$refs.viewer3d && this.$refs.viewer3d.compositePartTexture) {
        this.$nextTick(() => {
          this.$refs.viewer3d.compositePartTexture(part, id)
        })
      }
    },
    ...mapActions(useDesignStore, [ 
      'switchGarment', 
      'setCurrentSelectedPart',
      'saveCurrentGarmentData',
      'loadDesignFromData',
      'setPartColor',
      'addImageToPart',
      'addTextToPart',
      'updateLayerProperty',
      'deleteLayer',
      'clearLogoSelection',
      'selectLogo'
    ]),
    handleDirectPartSelect(part) {
      //  先更新 store.currentSelectedPart，确保 TextEditor 读取到正确的部位
      this.setCurrentSelectedPart(part)
      const viewer = this.$refs.viewer3d || window.$viewer3D
      if (viewer && typeof viewer.selectPart === 'function') {
        viewer.selectPart(part)
      } else {
        console.warn('Viewer3D not available for part selection:', part)
      }
    },
    loadHeaderFooter() {
      fetch('/header.html')
        .then(response => response.text())
        .then(data => {
          const header = document.getElementById('header-placeholder')
          if (header) header.innerHTML = data
        })
        .catch(error => console.error('Failed to load header:', error))
      fetch('/footer.html')
        .then(response => response.text())
        .then(data => {
          const footer = document.getElementById('footer-placeholder')
          if (footer) footer.innerHTML = data
        })
        .catch(error => console.error('Failed to load footer:', error))
    },

    handleSelectPart(part) {
      console.log('App: handleSelectPart called for', part, '(no-op, PartSelector handles directly)')
    },
    handleSelectColor(color) {
      if (this.currentSelectedPart) {
        this.setPartColor(this.currentSelectedPart, color)
      }
    },
    async handleAddImageToPart({ url, part, imageId }) {
      const newLayer = await this.addImageToPart(part, url, imageId)
      if (newLayer && newLayer.id && this.$refs.viewer3d) {
        this.$refs.viewer3d.currentEditingLogo = { part, id: newLayer.id, isText: false }
        this.$refs.viewer3d.compositePartTexture(part, newLayer.id)
        this.$refs.viewer3d.showTextureControls()
      }
    },
    handleAddText(textItem) {
      if (this.$refs.viewer3d && this.$refs.viewer3d.compositePartTexture) {
        this.$nextTick(() => {
          this.$refs.viewer3d.compositePartTexture(textItem.part, textItem.id)
        })
      }
    },
    handleUpdateText(textItem) {
      this.updateLayerProperty(textItem.part, textItem.id, 'textContent', textItem.text)
      this.updateLayerProperty(textItem.part, textItem.id, 'textFont', textItem.font)
      this.updateLayerProperty(textItem.part, textItem.id, 'textColor', textItem.color)
      this.updateLayerProperty(textItem.part, textItem.id, 'fontSize', textItem.fontSize)
      this.updateLayerProperty(textItem.part, textItem.id, 'scale', textItem.scale)
      this.updateLayerProperty(textItem.part, textItem.id, 'offsetX', textItem.offsetX)
      this.updateLayerProperty(textItem.part, textItem.id, 'offsetY', textItem.offsetY)
      this.updateLayerProperty(textItem.part, textItem.id, 'rotation', textItem.rotation)
      if (textItem.img) {
        this.updateLayerProperty(textItem.part, textItem.id, 'img', textItem.img)
      }
      if (this.$refs.viewer3d && this.$refs.viewer3d.compositePartTexture) {
        this.$refs.viewer3d.compositePartTexture(textItem.part, textItem.id)
      }
    },
    handleSelectLogo({ part, id, isText }) {
      this.selectLogo(part, id, isText)
    },
    handleClearLogoSelection() {
      this.clearLogoSelection()
    },
    handleDeleteText({ id, part }) {
      this.deleteLayer(part, id)
    },
    handleDeleteLogo({ id, part }) {
      this.deleteLayer(part, id)
    },
    handleLogoMoveEnd() {},
    handleModelLoaded({ partsMap }) {
      console.log('Model loaded with parts:', partsMap)
    },
    syncCurrentGarmentData() {
      this.saveCurrentGarmentData()
    },

    updateUrlWithDesignId(id) {
      if (!id) return
      const url = new URL(window.location.href)
      if (url.searchParams.get('id') !== id) {
        url.searchParams.set('id', id)
        window.history.replaceState({}, '', url.toString())
      }
    },

    async loadSharedDesign() {
      const urlParams = new URLSearchParams(window.location.search)
      const designId = urlParams.get('id')
      if (!designId) return

      const store = useDesignStore()
      
      // 防止重复加载
      if (store._isLoadingDesign) {
        console.log('Already loading design, skipping')
        return
      }
      store._isLoadingDesign = true
      
      store.currentDesignId = designId

      const loadingDiv = document.createElement('div')
      loadingDiv.id = 'designLoadingOverlay'
      loadingDiv.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.7);z-index:20001;display:flex;justify-content:center;align-items:center;color:white;font-size:18px;'
      loadingDiv.innerHTML = `<div style="background:#1a1a2e;border-radius:16px;padding:24px 32px;text-align:center;"><div>Loading your design...</div></div>`
      document.body.appendChild(loadingDiv)

      try {
        const response = await fetch(`load-design.php?id=${designId}`)
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const result = await response.json()

        if (!result.success || !result.all_garments) {
          console.error('Failed to load design:', result.message)
          loadingDiv.remove()
          store._isLoadingDesign = false
          return
        }

        // Short Sleeve 专属页面：始终固定为 Short Sleeve
        const targetGarment = 'Short Sleeve'
        console.log('目标款式（从后端）:', targetGarment)
        console.log('所有款式数据:', Object.keys(result.all_garments))

        // ★★★ 关键修复：先完整恢复所有款式数据到 garmentDesigns ★★★
        for (const [garmentType, garmentData] of Object.entries(result.all_garments)) {
          if (!store.garmentDesigns[garmentType]) {
            store.garmentDesigns[garmentType] = { colors: new Map(), layers: new Map(), texts: [], nextId: 1 }
          }
          
          const design = store.garmentDesigns[garmentType]
          
          // 恢复颜色
          design.colors.clear()
          if (garmentData.colors) {
            for (const [part, colorHex] of Object.entries(garmentData.colors)) {
              const colorNum = typeof colorHex === 'string' 
                ? parseInt(colorHex.substring(1), 16) 
                : colorHex
              design.colors.set(part, colorNum)
            }
          }
          
          // 恢复 layers
          design.layers.clear()
          
          // 恢复 logos
          if (garmentData.logos && garmentData.logos.length > 0) {
            for (const logo of garmentData.logos) {
              let layers = design.layers.get(logo.part) || []
              layers.push({
                id: logo.id,
                url: logo.url,
                scale: logo.scale || 1,
                offsetX: logo.offsetX || 0,
                offsetY: logo.offsetY || 0,
                rotation: logo.rotation || 0,
                isText: false
              })
              design.layers.set(logo.part, layers)
            }
          }
          
          // 恢复 texts
          if (garmentData.texts && garmentData.texts.length > 0) {
            for (const text of garmentData.texts) {
              let layers = design.layers.get(text.part) || []
              layers.push({
                id: text.id,
                scale: text.scale || 0.8,
                offsetX: text.offsetX || 0,
                offsetY: text.offsetY || 0,
                rotation: text.rotation || 0,
                isText: true,
                textContent: text.text,
                textFont: text.font,
                textColor: text.color,
                fontSize: text.fontSize || 80
              })
              design.layers.set(text.part, layers)
            }
            // 为每个文字项生成缩略图
            design.texts = garmentData.texts.map(t => ({
              ...t,
              imgSrc: t.imgSrc || store._createThumbCanvas(t.text, t.font, t.color, t.fontSize || 80)
            }))
          }
          
          // 更新 nextId
          let maxId = design.nextId || 1
          if (garmentData.texts) {
            garmentData.texts.forEach(t => { if (t.id && t.id > maxId) maxId = t.id })
          }
          if (garmentData.logos) {
            garmentData.logos.forEach(l => { if (l.id && l.id > maxId) maxId = l.id })
          }
          design.nextId = maxId + 1
          
          console.log(`款式 ${garmentType} 数据恢复完成: colors=${design.colors.size}, layers=${design.layers.size}, texts=${design.texts.length}`)
        }
        
        // 等待 Viewer3D 准备好
        await this.$nextTick()
        
        // ★★★ 关键修复：直接使用 switchGarment 来切换款式（这会从 garmentDesigns 恢复数据）★★★
        // 不要先调用 loadGarment，让 switchGarment 内部处理模型加载
        console.log(`切换到目标款式: ${targetGarment}`)
        
        // 先手动恢复目标款式的数据到当前状态
        const targetDesign = store.garmentDesigns[targetGarment]
        if (targetDesign) {
          console.log(`恢复 ${targetGarment} 的数据到当前状态`)
          
          // 恢复颜色
          store.userColors.clear()
          for (const [part, color] of targetDesign.colors.entries()) {
            store.userColors.set(part, color)
          }
          
          // 恢复图层
          store.partLayersMap.clear()
          for (const [part, layers] of targetDesign.layers.entries()) {
            store.partLayersMap.set(part, [...layers])
          }
          
          // 恢复文字 - 为每个文字项生成缩略图
          store.textItems = (targetDesign.texts || []).map(text => {
            // 始终重新生成缩略图，确保显示正确
            const imgSrc = store._createThumbCanvas(
              text.text || text.textContent,
              text.font || text.textFont,
              text.color || text.textColor,
              text.fontSize || 80
            )
            return { 
              ...text, 
              imgSrc,
              // 确保字段名统一
              text: text.text || text.textContent,
              font: text.font || text.textFont,
              color: text.color || text.textColor
            }
          })
          store.nextId = targetDesign.nextId
          store.currentGarment = targetGarment
          store.currentSelectedPart = 'front'
          
          // 强制触发响应式更新
          store.textItems = [...store.textItems]
          
          console.log(`当前款式已设置为 ${targetGarment}, userColors=${store.userColors.size}, partLayersMap=${store.partLayersMap.size}, textItems=${store.textItems.length}`)
        }
        
        // 加载模型（如已在加载中则等待，已加载完则直接合成纹理）
        if (this.$refs.viewer3d && this.$refs.viewer3d.loadGarment) {
          const viewer = this.$refs.viewer3d
          if (viewer.isLoading) {
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (!viewer.isLoading && viewer.partsMap && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          } else if (!viewer.currentModel || viewer.partsMap.size === 0) {
            viewer.loadGarment(targetGarment)
            await new Promise((resolve) => {
              const t = setInterval(() => {
                if (viewer.currentModel && viewer.partsMap.size > 0) {
                  clearInterval(t); resolve()
                }
              }, 100)
              setTimeout(() => { clearInterval(t); resolve() }, 8000)
            })
          }
        }

        // 等待数据恢复完成
        await new Promise(resolve => setTimeout(resolve, 200))
        
        // 强制刷新纹理
        if (this.$refs.viewer3d && this.$refs.viewer3d.partsMap) {
          for (const [part, layers] of store.partLayersMap.entries()) {
            if (layers.length > 0 && this.$refs.viewer3d.partsMap.has(part)) {
              for (const layer of layers) {
                if (layer.url && !layer.img) {
                  await new Promise((resolve) => {
                    const img = new Image()
                    img.crossOrigin = 'anonymous'
                    img.onload = () => {
                      layer.img = img
                      resolve()
                    }
                    img.onerror = () => resolve()
                    img.src = layer.url
                  })
                }
                if (layer.isText && layer.textContent && !layer.img) {
                  // 使用 store 中的 _createTextCanvas 方法来确保字体大小一致
                  const canvas = store._createTextCanvas(
                    layer.textContent,
                    layer.textFont || 'Arial',
                    layer.textColor || '#000000',
                    layer.fontSize || 80
                  )
                  const img = new Image()
                  img.src = canvas.toDataURL()
                  layer.img = img
                }
              }
              this.$refs.viewer3d.compositePartTexture(part)
            }
          }
          
          this.$refs.viewer3d.applyColorsToModel()
        }

        // 加载评论
        if (this.$refs.comments && typeof this.$refs.comments.loadComments === 'function') {
          this.$refs.comments.loadComments()
        }

        // 恢复 LOGO 缩略图列表
        if (this.$refs.logoUploader) {
          const logoUploader = this.$refs.logoUploader
          const restoredGallery = []
          for (const [, layers] of store.partLayersMap.entries()) {
            layers.forEach(layer => {
              if (!layer.isText && layer.url) {
                if (!restoredGallery.some(item => item.url === layer.url)) {
                  restoredGallery.push({
                    id: layer.id || (Date.now() + Math.random() * 10000),
                    url: layer.url
                  })
                }
              }
            })
          }
          if (restoredGallery.length > 0) {
            logoUploader.imageGallery = restoredGallery
            logoUploader.saveImageGallery()
          }
        }

// ★★★ 恢复 roster 数据 ★★★
if (result.roster && result.roster.length > 0) {
  store.rosterList = result.roster.map(m => ({
    name:     m.name     || '',
    gender:   m.gender   || 'Male',
    style:    m.style    || result.current_garment || 'Short Sleeve',
    size:     m.size     != null ? m.size : '',
    quantity: m.quantity != null && m.quantity !== '' ? m.quantity : '',
    message:  m.message  || ''
  }))
} else {
  store.rosterList = []
}

// 如果有 RosterManager 实例，刷新显示
if (this.$refs.rosterManager && this.$refs.rosterManager.loadFromStore) {
  await this.$nextTick()
  this.$refs.rosterManager.loadFromStore()
}

        console.log('Design loaded successfully:', designId)
        
      } catch (err) {
        console.error('加载设计失败:', err)
      } finally {
        if (document.getElementById('designLoadingOverlay')) {
          document.getElementById('designLoadingOverlay').remove()
        }
        store._isLoadingDesign = false
      }
    },



    toggleContactMenu() {
      this.contactMenuOpen = !this.contactMenuOpen
    },
    setupOutsideClick() {
      document.addEventListener('click', (e) => {
        const container = document.getElementById('floatingContact')
        if (container && !container.contains(e.target)) {
          this.contactMenuOpen = false
        }
      })
    },
    closeLoginPopup() {
      document.getElementById('loginOverlay').style.display = 'none'
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', sans-serif;
  background: #FFFFFF;
  color: #333333;
}

.container {
  width: 90%;
  max-width: 1400px;
  margin: 0 auto;
}

.designer-layout {
  display: flex;
  gap: 2rem;
  padding: 0.5rem 0;
  flex-wrap: wrap;
}

.viewer-wrapper {
  flex: 1;
  min-width: 280px;
  position: relative;
  overflow: hidden;
}

.right-panel {
  flex: 1;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: fit-content;
  background: transparent;
  border: none;
  padding: 0;
  padding-top: 0;
  margin-top: 0;
}

/* ========== 选项卡样式 ========== */
.design-tabs {
  display: flex;
  gap: 0.5rem;
  background: #ffffff;
  border-radius: 12px;
  padding: 0.15rem 0.25rem;
  margin-bottom: 0.5rem;
  margin-top: 0;
  border: 1px solid #e0e0e0;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 6px;
  border: 2px solid transparent;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn svg {
  stroke: white;
}

/* Design 按钮 - 默认背景 */
.tab-design {
  background: #3b82f6;
}

/* Review 按钮 - 默认背景 */
.tab-review {
  background: #61846C;
}

/* Roster 按钮 - 默认背景 */
.tab-roster {
  background: #DBB38A;
}

.tab-btn:hover {
  filter: brightness(1.05);
}

/* Design 选中状态 - 放大 + 边框高亮 */
.tab-design.active {
  background: #3b82f6;
  transform: scale(1.02);
  border: 2px solid #1e40af;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

/* Review 选中状态 - 放大 + 边框高亮 */
.tab-review.active {
  background: #10b981;
  transform: scale(1.02);
  border: 2px solid #065f46;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

/* Roster 选中状态 - 放大 + 边框高亮 */
.tab-roster.active {
  background: #FFC897;
  transform: scale(1.02);
  border: 2px solid #A5866A;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

/* 双箭头样式 */
.arrow-next {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  min-width: 24px;
}

.arrow-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  opacity: 0.7;
  transition: all 0.2s ease;
}

.arrow-next:hover .arrow-icon {
  opacity: 1;
  transform: translateX(2px);
}

/* 通用 active 样式（后备） */
.tab-btn.active {
  color: white;
}

.tab-btn.active svg {
  stroke: white;
}

.tab-btn.active svg {
  stroke: white;
}

/* ========== 标签页内容区域 ========== */
.tab-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
}

/* ========== 固定底部行动栏 ========== */
.fixed-action-bar {
  position: relative;
  margin-top: 1rem;
  padding-top: 0.5rem;
  background: transparent;
}

/* 移动端适配 */
@media (max-width: 850px) {
  .design-tabs {
    position: sticky;
    top: 0;
    z-index: 10;
    margin-bottom: 0.5rem;
  }
  
  .tab-btn {
    padding: 6px 6px;
    font-size: 0.75rem;
  }
  
  .tab-btn svg {
    width: 16px;
    height: 16px;
  }
  
  .tab-content {
    max-height: calc(100vh - 160px);
  }
  
  .fixed-action-bar {
    position: relative;
    background: #ffffff;
    padding: 0.5rem;
    margin-top: 0.5rem;
    border-top: 1px solid #e0e0e0;
    border-radius: 12px;
  }
}


/* right panel 上面的tools板块 */
.design-tools-section {
  background: #eff6ff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0;
}

/* right panel 下面的板块 */
.save-comments-section {
  background: #f0fdf4;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 1rem;
}
/* 标题右侧持久链接样式 */
.design-link-persistent {
  font-size: 0.7rem;
  color: #3b82f6;
  cursor: pointer;
  background: rgba(59, 130, 246, 0.1);
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
}
.design-link-persistent:hover {
  background: rgba(59, 130, 246, 0.2);
}

/* 内部各模块间距 */
.design-tools-section .category-block,
.save-comments-section .category-block {
  margin-bottom: 0.8rem;
}

/* SaveButton 样式调整为块级（不再绝对定位） */
.save-design-corner-btn {
  position: relative;
  top: auto;
  right: auto;
  margin-bottom: 1rem;
  width: 100%;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
}


.top-group,
.bottom-group {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.login-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: none;
  justify-content: center;
  align-items: center;
}

.login-popup {
  background: #1a202c;
  border-radius: 20px;
  padding: 2rem;
  width: 90%;
  max-width: 400px;
  text-align: center;
  border: 1px solid #3b82f6;
  position: relative;
}

.login-popup h3 {
  color: white;
  margin-bottom: 1rem;
}

.login-popup p {
  color: #ccc;
  margin-bottom: 1.5rem;
}

.login-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.login-btn {
  padding: 0.5rem 1.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: bold;
}

.login-btn.register {
  background: #3b82f6;
  color: white;
}

.login-btn.login {
  background: #22c55e;
  color: white;
}

.close-popup {
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
}

.floating-contact {
  position: fixed;
  bottom: 80px;
  right: 20px;
  z-index: 1000;
}

.contact-toggle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #25d366;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
}

.contact-toggle:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.5);
}

.contact-options {
  position: absolute;
  bottom: 70px;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.contact-options.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.contact-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border-radius: 24px;
  text-decoration: none;
  color: #333;
  font-size: 0.85rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
  white-space: nowrap;
}

.contact-option:hover {
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.contact-option.whatsapp { color: #25d366; }
.contact-option.email { color: #ea4335; }
.contact-option.phone { color: #4285f4; }
.contact-option i { font-size: 1.2rem; }

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 850px) {
  .designer-layout {
    gap: 0.5rem;
    padding: 0.5rem 0;
  }
  .container {
    width: 100%;
    padding: 0;
  }
  .viewer-wrapper {
    margin-bottom: 0.5rem;
  }
  .right-panel {
    margin: 0;
    border-radius: 0;
    padding: 0;
    padding-top: 0;
    gap: 0.5rem;
    margin-bottom: 30px;
    padding-bottom: 20px;
  }
  .design-tools-section {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  
  .save-comments-section {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }


}

@media (max-width: 768px) {
  .floating-contact {
    bottom: 70px;
    right: 12px;
  }
  .contact-toggle {
    width: 48px;
    height: 48px;
  }
  .design-tools-section {
      padding-left: 0.25rem !important;
      padding-right: 0.25rem !important;
    }
    .save-comments-section {
      padding-left: 0.25rem !important;
      padding-right: 0.25rem !important;
    }
}
</style>